import jsPDF from "jspdf";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  PageBreak,
  Table,
  TableRow,
  TableCell,
  WidthType,
} from "docx";
import type { Test, Question, Blueprint } from "@shared/schema";
import { readingLevelDisplayLabel } from "@shared/schema";
import { levelLabel } from "./levelLabels";
import { formatSourceAnchors } from "./sourceAnchors";

// Success green (#10B981) for the correct-answer checkmark.
const CHECK_GREEN: [number, number, number] = [16, 185, 129];

// Shared rendering + downloading code for tests. Lives here (not on the
// Share page) so the Dashboard's "Download…" action can produce the same
// PDF / DOCX / TXT / MD output as the finalize-flow Share screen, from a
// `Test` record fetched via `/api/tests` — without going through the test
// context or routing back into the workflow.

export type TestSection = "blueprint" | "studentCopy" | "answerKey" | "teacherCopy";

export const SECTION_LABEL: Record<TestSection, string> = {
  blueprint: "Assessment Blueprint",
  studentCopy: "Student Copy",
  answerKey: "Answer Key",
  teacherCopy: "Teacher Copy",
};

export const SECTION_SUFFIX: Record<TestSection, string> = {
  blueprint: "Assessment_Blueprint",
  studentCopy: "Student_Copy",
  answerKey: "Answer_Key",
  teacherCopy: "Teacher_Copy",
};

// Test-like input. The Share page passes loose pieces from TestContext,
// while the Dashboard passes a full Test from the server. Either way we
// only read these four bits.
export interface TestLike {
  name: string;
  blueprint: Blueprint | null | undefined;
  questions: Question[];
  directions: string | null | undefined;
  // Used by the Assessment Blueprint header "Grade".
  // Optional so callers that don't have a TestConfig can omit them.
  subject?: string;
  readingLevel?: string;
}

const DEFAULT_DIRECTIONS = "Please read each question carefully and select the best answer.";

// Display letter for an answer option (0 -> "A", …). Supports option lists
// longer than the original 4.
const answerLabel = (index: number): string => String.fromCharCode(65 + index);

// ---------------------------------------------------------------------------
// Assessment Blueprint model. Reflects the FINAL approved assessment: every
// per-topic / per-level count comes from the actual questions array (after
// review/edits/removals), not the planned blueprint numbers. Shared by every
// export format (TXT/MD/PDF/DOCX).
// ---------------------------------------------------------------------------

function courseGradeLabel(t: TestLike): string {
  return t.readingLevel ? readingLevelDisplayLabel(t.readingLevel) : "—";
}

// Integer percentages that sum to exactly 100 (largest-remainder method).
function percentsSummingTo100(counts: number[], total: number): number[] {
  if (total <= 0) return counts.map(() => 0);
  const raw = counts.map((c) => (c / total) * 100);
  const result = raw.map((r) => Math.floor(r));
  let remainder = 100 - result.reduce((s, n) => s + n, 0);
  const order = raw.map((r, i) => ({ i, frac: r - Math.floor(r) })).sort((a, b) => b.frac - a.frac);
  for (let k = 0; k < order.length && remainder > 0; k++, remainder--) result[order[k].i]++;
  return result;
}

function topicNameForId(t: TestLike, topicId: string): string {
  return t.blueprint?.topics.find((tp) => tp.id === topicId)?.name || "Unknown Topic";
}

interface BlueprintModel {
  header: { title: string; courseGrade: string; totalItems: number; itemType: string; date: string };
  summaryRows: { name: string; knowledge: number; understanding: number; higherOrder: number; total: number; weight: number }[];
  totals: { knowledge: number; understanding: number; higherOrder: number; total: number };
  // Item Map: clean three-column alignment of topic, item number, taxonomic
  // level. Item Focus / Skill and Correct Answer were intentionally dropped
  // — correct answers belong in the answer key download, full item text in
  // the test download.
  itemMapRows: { num: number; topic: string; level: string }[];
}

function buildBlueprintModel(t: TestLike): BlueprintModel {
  const totalItems = t.questions.length;
  const topics = t.blueprint?.topics || [];
  const baseRows = topics.map((tp) => {
    const knowledge = t.questions.filter((q) => q.topicId === tp.id && q.level === "Knowledge").length;
    const understanding = t.questions.filter((q) => q.topicId === tp.id && q.level === "Understanding").length;
    const higherOrder = t.questions.filter((q) => q.topicId === tp.id && q.level === "Higher-Order Thinking").length;
    return { name: tp.name, knowledge, understanding, higherOrder, total: knowledge + understanding + higherOrder };
  });
  const weights = percentsSummingTo100(baseRows.map((r) => r.total), totalItems);
  const summaryRows = baseRows.map((r, i) => ({ ...r, weight: weights[i] }));
  const totals = {
    knowledge: summaryRows.reduce((s, r) => s + r.knowledge, 0),
    understanding: summaryRows.reduce((s, r) => s + r.understanding, 0),
    higherOrder: summaryRows.reduce((s, r) => s + r.higherOrder, 0),
    total: summaryRows.reduce((s, r) => s + r.total, 0),
  };
  const itemMapRows = t.questions.map((q, i) => ({
    num: i + 1,
    topic: topicNameForId(t, q.topicId),
    level: levelLabel(q.level),
  }));
  return {
    header: {
      title: t.name || "Untitled Assessment",
      courseGrade: courseGradeLabel(t),
      totalItems,
      itemType: "Multiple Choice",
      date: new Date().toLocaleDateString(),
    },
    summaryRows,
    totals,
    itemMapRows,
  };
}

const SUMMARY_HEADERS = ["Topic / Content Area", "Content Weight", "Knowledge", "Understanding", "Application+", "Total Items"];
const ITEM_MAP_HEADERS = ["Topic / Content Area", "Item #", "Taxonomic Level"];

function blueprintTableRows(m: BlueprintModel): { summary: string[][]; itemMap: string[][] } {
  const summary = m.summaryRows.map((r) => [r.name, `${r.weight}%`, String(r.knowledge), String(r.understanding), String(r.higherOrder), String(r.total)]);
  summary.push(["Total", "100%", String(m.totals.knowledge), String(m.totals.understanding), String(m.totals.higherOrder), String(m.totals.total)]);
  const itemMap = m.itemMapRows.map((r) => [r.topic, String(r.num), r.level]);
  return { summary, itemMap };
}

// ---------------------------------------------------------------------------
// Plain text generators — also used as the body for TXT and MD downloads.
// ---------------------------------------------------------------------------

export function generateBlueprintContent(t: TestLike, format: "txt" | "md" = "txt"): string {
  const isMd = format === "md";
  const m = buildBlueprintModel(t);
  const { summary, itemMap } = blueprintTableRows(m);

  const mdTable = (headers: string[], rows: string[][]) =>
    [
      `| ${headers.join(" | ")} |`,
      `| ${headers.map(() => "---").join(" | ")} |`,
      ...rows.map((r) => `| ${r.join(" | ")} |`),
    ].join("\n");
  const textTable = (headers: string[], rows: string[][]) => {
    const widths = headers.map((h, c) => Math.max(h.length, ...rows.map((r) => (r[c] || "").length)));
    const fmt = (cells: string[]) => cells.map((cell, c) => (cell || "").padEnd(widths[c])).join("  ");
    return [fmt(headers), widths.map((w) => "-".repeat(w)).join("  "), ...rows.map(fmt)].join("\n");
  };
  const table = isMd ? mdTable : textTable;

  const intro =
    "This blueprint documents the topics and taxonomic levels covered by this assessment as written.";
  const header = isMd
    ? [
        "# Assessment Blueprint",
        "",
        `**Assessment Title:** ${m.header.title}`,
        `**Grade:** ${m.header.courseGrade}`,
        `**Total Items:** ${m.header.totalItems}`,
        `**Item Type:** ${m.header.itemType}`,
        `**Date Generated:** ${m.header.date}`,
        "",
        intro,
      ].join("\n")
    : [
        "ASSESSMENT BLUEPRINT",
        "====================",
        "",
        `Assessment Title: ${m.header.title}`,
        `Grade:   ${m.header.courseGrade}`,
        `Total Items:      ${m.header.totalItems}`,
        `Item Type:        ${m.header.itemType}`,
        `Date Generated:   ${m.header.date}`,
        "",
        intro,
      ].join("\n");

  return [
    header,
    "",
    isMd ? "## Blueprint Summary" : "BLUEPRINT SUMMARY\n-----------------",
    "",
    table(SUMMARY_HEADERS, summary),
    "",
    isMd ? "## Item Map" : "ITEM MAP\n--------",
    "",
    table(ITEM_MAP_HEADERS, itemMap),
    "",
  ].join("\n");
}

export function generateStudentCopyContent(t: TestLike): string {
  return `${t.name}
${"=".repeat(t.name.length)}

${t.directions || DEFAULT_DIRECTIONS}

${t.questions
  .map((question, index) => {
    return `${index + 1}. ${question.stem}

${question.answers
  .map((answer, ansIndex) => `   ${answerLabel(ansIndex)}) ${answer}`)
  .join("\n")}

`;
  })
  .join("\n\n\n")}`;
}

export function generateAnswerKeyContent(t: TestLike): string {
  return `ANSWER KEY - ${t.name}
${"=".repeat(`ANSWER KEY - ${t.name}`.length)}

${t.questions
  .map(
    (question, index) =>
      `✓ ${index + 1}. ${answerLabel(question.correctAnswer)} - ${question.answers[question.correctAnswer]}`,
  )
  .join("\n")}

SCORING BREAKDOWN:
${t.questions
  .map((question, index) => {
    const topicName = t.blueprint?.topics.find((tp) => tp.id === question.topicId)?.name || "Unknown Topic";
    return `Q${index + 1}: ${levelLabel(question.level)} (${topicName})`;
  })
  .join("\n")}
`;
}

export function generateTeacherCopyContent(t: TestLike): string {
  return `TEACHER COPY - ${t.name}
${"=".repeat(`TEACHER COPY - ${t.name}`.length)}

${t.directions || DEFAULT_DIRECTIONS}

${t.questions
  .map((question, index) => {
    const topic = t.blueprint?.topics.find((tp) => tp.id === question.topicId);
    const provenance = formatSourceAnchors(topic?.sourceAnchors);
    return `${index + 1}. ${question.stem}
   Topic: ${topic?.name || "Unknown Topic"} | Level: ${levelLabel(question.level)}${provenance ? `\n   ${provenance}` : ""}

${question.answers
  .map(
    (answer, ansIndex) =>
      `${ansIndex === question.correctAnswer ? "✓  " : "   "}${answerLabel(ansIndex)}) ${answer}`,
  )
  .join("\n")}

`;
  })
  .join("\n")}`;
}

export function generateSectionContent(t: TestLike, section: TestSection, format: "txt" | "md" = "txt"): string {
  switch (section) {
    case "blueprint":
      return generateBlueprintContent(t, format);
    case "studentCopy":
      return generateStudentCopyContent(t);
    case "answerKey":
      return generateAnswerKeyContent(t);
    case "teacherCopy":
      return generateTeacherCopyContent(t);
  }
}

// ---------------------------------------------------------------------------
// PDF rendering — jsPDF on US Letter with measured page breaks so individual
// questions don't split across pages.
// ---------------------------------------------------------------------------

export function buildTestPdf(t: TestLike, sections: TestSection[]): Blob {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 54; // 0.75"
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const ensureRoom = (needed: number) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  type WrapOpts = { size: number; bold?: boolean; indent?: number; lineGap?: number };

  const measureWrapped = (text: string, opts: WrapOpts): number => {
    doc.setFont("helvetica", opts.bold ? "bold" : "normal");
    doc.setFontSize(opts.size);
    const indent = opts.indent ?? 0;
    const lineGap = opts.lineGap ?? opts.size * 0.4;
    const lineHeight = opts.size * 1.15;
    const lines = doc.splitTextToSize(text, contentWidth - indent);
    return lines.length * lineHeight + lineGap;
  };

  const writeWrapped = (text: string, opts: WrapOpts) => {
    doc.setFont("helvetica", opts.bold ? "bold" : "normal");
    doc.setFontSize(opts.size);
    const indent = opts.indent ?? 0;
    const lineGap = opts.lineGap ?? opts.size * 0.4;
    const lineHeight = opts.size * 1.15;
    const lines = doc.splitTextToSize(text, contentWidth - indent);
    for (const line of lines) {
      ensureRoom(lineHeight);
      doc.text(line, margin + indent, y);
      y += lineHeight;
    }
    y += lineGap;
  };

  // Draw a small green checkmark with its tip at the text baseline (y).
  const drawGreenCheck = (x: number, baselineY: number, size: number) => {
    const h = size * 0.6;
    doc.setDrawColor(...CHECK_GREEN);
    doc.setLineWidth(Math.max(1, size * 0.11));
    // Short down-stroke into the vertex, then the long up-stroke.
    doc.line(x, baselineY - h * 0.4, x + h * 0.4, baselineY);
    doc.line(x + h * 0.4, baselineY, x + h, baselineY - h);
    doc.setDrawColor(0, 0, 0);
  };

  // Like writeWrapped, but renders normal-weight and stamps a green check in
  // the left gutter of the first line — used for correct teacher answers and
  // answer-key rows. The text carries leading spaces so the check sits to the
  // left of the option/number without disturbing column alignment.
  const writeWrappedWithCheck = (text: string, opts: WrapOpts) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(opts.size);
    const indent = opts.indent ?? 0;
    const lineGap = opts.lineGap ?? opts.size * 0.4;
    const lineHeight = opts.size * 1.15;
    const lines = doc.splitTextToSize(text, contentWidth - indent) as string[];
    let first = true;
    for (const line of lines) {
      ensureRoom(lineHeight);
      if (first) {
        drawGreenCheck(margin + indent, y, opts.size);
        first = false;
      }
      doc.text(line, margin + indent, y);
      y += lineHeight;
    }
    y += lineGap;
  };

  const sectionTitle = (text: string) => {
    if (y > margin) {
      doc.addPage();
      y = margin;
    }
    writeWrapped(text, { size: 20, bold: true, lineGap: 12 });
  };

  // Bordered table with wrapping cells; the header row repeats across pages.
  const drawTable = (headers: string[], rows: string[][], colWidths: number[]) => {
    const fontSize = 9;
    const lineHeight = fontSize * 1.15;
    const padX = 4;
    const padY = 4;
    const colX: number[] = [];
    let acc = margin;
    for (let c = 0; c < colWidths.length; c++) {
      colX[c] = acc;
      acc += colWidths[c];
    }
    const tableRight = acc;

    const layoutRow = (cells: string[], bold: boolean) => {
      const wrapped = cells.map((cell, c) => {
        doc.setFont("helvetica", bold ? "bold" : "normal");
        doc.setFontSize(fontSize);
        return doc.splitTextToSize(cell || "", colWidths[c] - padX * 2) as string[];
      });
      const lineCount = Math.max(1, ...wrapped.map((w) => w.length));
      return { wrapped, height: lineCount * lineHeight + padY * 2 };
    };

    const paintRow = (wrapped: string[][], height: number, bold: boolean, fill: boolean) => {
      const top = y;
      if (fill) {
        doc.setFillColor(241, 245, 249);
        doc.rect(margin, top, tableRight - margin, height, "F");
      }
      doc.setDrawColor(203, 213, 225);
      doc.setLineWidth(0.5);
      doc.setFontSize(fontSize);
      wrapped.forEach((lines, c) => {
        doc.rect(colX[c], top, colWidths[c], height);
        doc.setFont("helvetica", bold ? "bold" : "normal");
        lines.forEach((ln, li) => {
          doc.text(ln, colX[c] + padX, top + padY + fontSize * 0.9 + lineHeight * li);
        });
      });
      y += height;
    };

    const drawHeader = () => {
      const { wrapped, height } = layoutRow(headers, true);
      ensureRoom(height);
      paintRow(wrapped, height, true, true);
    };

    drawHeader();
    rows.forEach((r) => {
      const { wrapped, height } = layoutRow(r, false);
      if (y + height > pageHeight - margin) {
        doc.addPage();
        y = margin;
        drawHeader();
      }
      paintRow(wrapped, height, false, false);
    });
    y += 10;
  };

  const renderBlueprint = () => {
    const m = buildBlueprintModel(t);
    const { summary, itemMap } = blueprintTableRows(m);

    sectionTitle("Assessment Blueprint");
    writeWrapped(`Assessment Title: ${m.header.title}`, { size: 11, lineGap: 2 });
    writeWrapped(`Grade: ${m.header.courseGrade}`, { size: 11, lineGap: 2 });
    writeWrapped(`Total Items: ${m.header.totalItems}`, { size: 11, lineGap: 2 });
    writeWrapped(`Item Type: ${m.header.itemType}`, { size: 11, lineGap: 2 });
    writeWrapped(`Date Generated: ${m.header.date}`, { size: 11, lineGap: 8 });
    writeWrapped(
      "This blueprint documents the topics and taxonomic levels covered by this assessment as written.",
      { size: 10, lineGap: 14 },
    );

    writeWrapped("Blueprint Summary", { size: 14, bold: true, lineGap: 8 });
    drawTable(SUMMARY_HEADERS, summary, [150, 70, 60, 80, 84, 60]);

    y += 16; // breathing room between the summary table and the next heading
    writeWrapped("Item Map", { size: 14, bold: true, lineGap: 8 });
    // Topic gets the bulk of the width (long names), Item # is narrow,
    // Taxonomic Level sits in the middle. Total matches what the original
    // five-column alignment table used.
    drawTable(ITEM_MAP_HEADERS, itemMap, [300, 50, 154]);
  };

  const renderQuestion = (q: Question, index: number, mode: "student" | "teacher" | "key") => {
    type Part = { text: string; opts: WrapOpts; check?: boolean };
    const parts: Part[] = [];

    if (mode === "key") {
      // Every key row is the correct answer — mark each with a left green check.
      parts.push({
        text: `   ${index + 1}. ${answerLabel(q.correctAnswer)} — ${q.answers[q.correctAnswer]}`,
        opts: { size: 11, lineGap: 4 },
        check: true,
      });
    } else {
      parts.push({ text: `${index + 1}. ${q.stem}`, opts: { size: 11, bold: mode !== "student", lineGap: 4 } });
      q.answers.forEach((answer, ansIdx) => {
        const isCorrect = mode === "teacher" && ansIdx === q.correctAnswer;
        // Teacher copy marks the correct answer with a drawn green check (see
        // writeWrappedWithCheck) rather than bold text or a "(correct)" tag.
        parts.push({
          text: `   ${answerLabel(ansIdx)}) ${answer}`,
          opts: { size: 11, indent: 12, lineGap: 2 },
          check: isCorrect,
        });
      });
      if (mode === "teacher") {
        const topic = t.blueprint?.topics.find((tp) => tp.id === q.topicId);
        const provenance = formatSourceAnchors(topic?.sourceAnchors);
        parts.push({
          text: `Topic: ${topic?.name || "Unknown"}  •  Level: ${levelLabel(q.level)}`,
          opts: { size: 9, indent: 12, lineGap: provenance ? 2 : 8 },
        });
        if (provenance) {
          parts.push({ text: provenance, opts: { size: 9, indent: 12, lineGap: 8 } });
        }
      }
    }

    // Atomic page break: if the whole question would split AND fits on a
    // single page, push it to the next page first.
    const needed = parts.reduce((sum, p) => sum + measureWrapped(p.text, p.opts), 0);
    const pageContentHeight = pageHeight - margin * 2;
    if (needed <= pageContentHeight && y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }

    for (const p of parts) {
      if (p.check) writeWrappedWithCheck(p.text, p.opts);
      else writeWrapped(p.text, p.opts);
    }
    if (mode === "student") y += 28;
  };

  const renderStudentCopy = () => {
    sectionTitle(t.name);
    writeWrapped(t.directions || DEFAULT_DIRECTIONS, { size: 11, lineGap: 12 });
    t.questions.forEach((q, idx) => renderQuestion(q, idx, "student"));
  };

  const renderAnswerKey = () => {
    sectionTitle(`Answer Key — ${t.name}`);
    t.questions.forEach((q, idx) => renderQuestion(q, idx, "key"));
  };

  const renderTeacherCopy = () => {
    sectionTitle(`Teacher Copy — ${t.name}`);
    writeWrapped(t.directions || DEFAULT_DIRECTIONS, { size: 11, lineGap: 12 });
    t.questions.forEach((q, idx) => renderQuestion(q, idx, "teacher"));
  };

  const renderers: Record<TestSection, () => void> = {
    blueprint: renderBlueprint,
    studentCopy: renderStudentCopy,
    answerKey: renderAnswerKey,
    teacherCopy: renderTeacherCopy,
  };

  sections.forEach((s, i) => {
    if (i === 0) {
      y = margin;
    }
    renderers[s]();
  });

  return doc.output("blob");
}

// ---------------------------------------------------------------------------
// DOCX rendering via the `docx` builder. keepNext on question stems keeps
// them together with their answer choices on the same page.
// ---------------------------------------------------------------------------

export async function buildTestDocx(t: TestLike, sections: TestSection[]): Promise<Blob> {
  const heading = (text: string) =>
    new Paragraph({ text, heading: HeadingLevel.HEADING_1, spacing: { after: 200 } });
  const subheading = (text: string) =>
    new Paragraph({ text, heading: HeadingLevel.HEADING_2, spacing: { before: 120, after: 80 } });
  const body = (text: string, opts?: { bold?: boolean; indent?: number; keepNext?: boolean }) =>
    new Paragraph({
      children: [new TextRun({ text, bold: opts?.bold })],
      indent: opts?.indent ? { left: opts.indent } : undefined,
      keepNext: opts?.keepNext,
      spacing: { after: 60 },
    });
  const bullet = (text: string) =>
    new Paragraph({ text, bullet: { level: 0 }, spacing: { after: 40 } });

  const questionParagraphs = (q: Question, index: number, mode: "student" | "teacher" | "key"): Paragraph[] => {
    if (mode === "key") {
      // Every key row is the correct answer — mark each with a left green check.
      return [
        new Paragraph({
          children: [
            new TextRun({ text: "✓ ", color: "10B981" }),
            new TextRun({ text: `${index + 1}. `, bold: true }),
            new TextRun({ text: `${answerLabel(q.correctAnswer)} — ${q.answers[q.correctAnswer]}` }),
          ],
          spacing: { after: 80 },
        }),
      ];
    }

    const paras: Paragraph[] = [];
    paras.push(
      new Paragraph({
        children: [new TextRun({ text: `${index + 1}. ${q.stem}`, bold: mode !== "student" })],
        keepNext: true,
        spacing: { before: mode === "student" ? 440 : 120, after: 60 },
      }),
    );
    q.answers.forEach((answer, ansIdx) => {
      const isCorrect = mode === "teacher" && ansIdx === q.correctAnswer;
      const isLastBeforeFooter = mode === "teacher" && ansIdx === q.answers.length - 1;
      paras.push(
        new Paragraph({
          // Answer stays normal weight; the correct one gets a leading green
          // check (no bold, no "(correct)"). The "✓ + 2 spaces" vs "3 spaces"
          // keeps the option letters column-aligned.
          children: isCorrect
            ? [
                new TextRun({ text: "✓", color: "10B981" }),
                new TextRun({ text: `  ${answerLabel(ansIdx)}) ${answer}` }),
              ]
            : [new TextRun({ text: `   ${answerLabel(ansIdx)}) ${answer}` })],
          indent: { left: 240 },
          keepNext:
            mode === "teacher" && isLastBeforeFooter ? true : ansIdx < q.answers.length - 1,
          spacing: { after: 40 },
        }),
      );
    });
    if (mode === "teacher") {
      const topic = t.blueprint?.topics.find((tp) => tp.id === q.topicId);
      const provenance = formatSourceAnchors(topic?.sourceAnchors);
      paras.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `Topic: ${topic?.name || "Unknown"}  •  Level: ${levelLabel(q.level)}`,
              italics: true,
              size: 18,
              color: "666666",
            }),
          ],
          indent: { left: 240 },
          spacing: { after: provenance ? 20 : 120 },
        }),
      );
      if (provenance) {
        paras.push(
          new Paragraph({
            children: [new TextRun({ text: provenance, italics: true, size: 18, color: "666666" })],
            indent: { left: 240 },
            spacing: { after: 120 },
          }),
        );
      }
    }
    return paras;
  };

  const docxTable = (headers: string[], rows: string[][], widths: number[]): Table => {
    const cell = (text: string, bold: boolean, w: number) =>
      new TableCell({
        width: { size: w, type: WidthType.DXA },
        children: [new Paragraph({ children: [new TextRun({ text, bold, size: 18 })] })],
      });
    const headerRow = new TableRow({ tableHeader: true, children: headers.map((h, i) => cell(h, true, widths[i])) });
    const bodyRows = rows.map((r) => new TableRow({ children: r.map((c, i) => cell(c, false, widths[i])) }));
    return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      columnWidths: widths,
      rows: [headerRow, ...bodyRows],
    });
  };

  const blueprintParagraphs = (): (Paragraph | Table)[] => {
    const m = buildBlueprintModel(t);
    const { summary, itemMap } = blueprintTableRows(m);
    const out: (Paragraph | Table)[] = [];
    out.push(heading("Assessment Blueprint"));
    out.push(body(`Assessment Title: ${m.header.title}`, { bold: true }));
    out.push(body(`Grade: ${m.header.courseGrade}`));
    out.push(body(`Total Items: ${m.header.totalItems}`));
    out.push(body(`Item Type: ${m.header.itemType}`));
    out.push(body(`Date Generated: ${m.header.date}`));
    out.push(
      body("This blueprint documents the topics and taxonomic levels covered by this assessment as written."),
    );
    out.push(subheading("Blueprint Summary"));
    out.push(docxTable(SUMMARY_HEADERS, summary, [2800, 1300, 1000, 1300, 1660, 1300]));
    out.push(subheading("Item Map"));
    // Topic widest (long names), Item # narrow, Taxonomic Level mid.
    // Total ~ matches the original five-column alignment table width.
    out.push(docxTable(ITEM_MAP_HEADERS, itemMap, [5400, 1200, 2760]));
    return out;
  };

  const studentParagraphs = (): Paragraph[] => {
    const paras: Paragraph[] = [];
    paras.push(heading(t.name));
    paras.push(body(t.directions || DEFAULT_DIRECTIONS));
    t.questions.forEach((q, idx) => {
      paras.push(...questionParagraphs(q, idx, "student"));
    });
    return paras;
  };

  const answerKeyParagraphs = (): Paragraph[] => {
    const paras: Paragraph[] = [];
    paras.push(heading(`Answer Key — ${t.name}`));
    t.questions.forEach((q, idx) => {
      paras.push(...questionParagraphs(q, idx, "key"));
    });
    return paras;
  };

  const teacherParagraphs = (): Paragraph[] => {
    const paras: Paragraph[] = [];
    paras.push(heading(`Teacher Copy — ${t.name}`));
    paras.push(body(t.directions || DEFAULT_DIRECTIONS));
    t.questions.forEach((q, idx) => {
      paras.push(...questionParagraphs(q, idx, "teacher"));
    });
    return paras;
  };

  const sectionRenderers: Record<TestSection, () => (Paragraph | Table)[]> = {
    blueprint: blueprintParagraphs,
    studentCopy: studentParagraphs,
    answerKey: answerKeyParagraphs,
    teacherCopy: teacherParagraphs,
  };

  const children: (Paragraph | Table)[] = [];
  sections.forEach((s, i) => {
    if (i > 0) {
      children.push(new Paragraph({ children: [new PageBreak()] }));
    }
    children.push(...sectionRenderers[s]());
  });

  const doc = new Document({ sections: [{ properties: {}, children }] });
  return await Packer.toBlob(doc);
}

// ---------------------------------------------------------------------------
// Trigger a browser download for any Blob. Cleans up the object URL after.
// ---------------------------------------------------------------------------

export function downloadBlob(blob: Blob, filename: string, extension: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}.${extension}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function safeFilenameBase(name: string): string {
  return name.replace(/[^a-zA-Z0-9]/g, "_") || "Test";
}

export function textMimeType(format: "txt" | "md"): string {
  return format === "md" ? "text/markdown" : "text/plain";
}

// ---------------------------------------------------------------------------
// All-in-one download: takes a TestLike, the picked sections, format, and
// single-vs-multi-file toggle, and produces the downloads. Used by both the
// finalize Share page and the Dashboard's Download… dialog.
// ---------------------------------------------------------------------------

export type DownloadFormat = "pdf" | "docx" | "txt" | "md";

export interface DownloadOptions {
  format: DownloadFormat;
  sections: TestSection[];
  singleFile: boolean;
  // Optional custom base name. When set, the file is named exactly this — no
  // "_Complete_Test" and no section label — except that downloading several
  // SEPARATE files appends a section label so they don't collide. Without it,
  // the legacy naming (test name + descriptive suffix) is used.
  filenameBase?: string;
}

export async function downloadTest(t: TestLike, opts: DownloadOptions): Promise<void> {
  const { format, sections, singleFile, filenameBase } = opts;
  if (sections.length === 0) return;

  const custom = !!(filenameBase && filenameBase.trim());
  // Custom names are honored verbatim (strip only OS-illegal characters); the
  // default path keeps the legacy underscore sanitization for the Share page.
  const safeBase = custom
    ? filenameBase!.replace(/[/\\:*?"<>|]/g, "").replace(/\s+/g, " ").trim() || "Test"
    : safeFilenameBase(t.name);

  // One combined file → exactly the custom name (legacy: + "_Complete_Test").
  const combinedName = custom ? safeBase : `${safeBase}_Complete_Test`;
  // A separate per-section file is always labeled (Student Copy, Answer Key, …)
  // so it's clear which copy it is.
  const sectionName = (s: TestSection) => `${safeBase}_${SECTION_SUFFIX[s]}`;

  if (format === "pdf") {
    if (singleFile) {
      downloadBlob(buildTestPdf(t, sections), combinedName, "pdf");
    } else {
      for (const s of sections) downloadBlob(buildTestPdf(t, [s]), sectionName(s), "pdf");
    }
    return;
  }

  if (format === "docx") {
    if (singleFile) {
      downloadBlob(await buildTestDocx(t, sections), combinedName, "docx");
    } else {
      for (const s of sections) downloadBlob(await buildTestDocx(t, [s]), sectionName(s), "docx");
    }
    return;
  }

  // TXT / MD path.
  const ext = format;
  const mime = textMimeType(format);
  if (singleFile) {
    const combined = sections
      .map(
        (s) =>
          `${"=".repeat(50)}\n${SECTION_LABEL[s].toUpperCase()}\n${"=".repeat(50)}\n\n${generateSectionContent(t, s, format)}\n\n`,
      )
      .join("\n");
    downloadBlob(new Blob([combined], { type: mime }), combinedName, ext);
  } else {
    for (const s of sections) {
      downloadBlob(new Blob([generateSectionContent(t, s, format)], { type: mime }), sectionName(s), ext);
    }
  }
}

// Adapter so callers with a full `Test` record from the server can drop
// straight into the download API without re-shaping.
export function testToTestLike(test: Test): TestLike {
  return {
    name: test.name,
    blueprint: test.blueprint,
    questions: test.questions,
    directions: test.directions,
    subject: test.config?.subject,
    readingLevel: test.config?.readingLevel,
  };
}
